import {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useSelector} from 'react-redux'
import {useTheme} from '@mui/material/styles'
import RangeFilterControl from './RangeFilterControl'
import CheckboxFilterControl from './CheckboxFilterControl'
import RadioFilterControl from './RadioFilterControl'
import OutreachFilterControl from './OutreachFilterControl'
import {selectFilterConfig} from '../../../store/slices/configSlice'
import {useOutreachFilters} from '../../../store/hooks/useOutreachFilters'

/**
 * Wrapper for OutreachFilterControl that handles permissions and mutual exclusivity
 *
 * - No permission: Option is NOT rendered (hidden)
 * - Mutually exclusive (eligibility filter active): Option is disabled with tooltip
 */
const OutreachFilterControlWrapper = ({filterKey, options, categoryTitle, ...props}) => {
  const {benefitsOptionsWithState, workforceOptionsWithState} = useOutreachFilters()

  // Get the appropriate options with state based on filter key
  const optionsWithState = useMemo(() => {
    if (filterKey === 'receivingBenefits') {
      return benefitsOptionsWithState
    } else if (filterKey === 'workforceRecruitment') {
      return workforceOptionsWithState
    }
    // Fallback: return options as-is with default state
    return options.map((opt) => ({...opt, isEnabled: true, isDisabled: false, disabledReason: null}))
  }, [filterKey, benefitsOptionsWithState, workforceOptionsWithState, options])

  // Filter options: Only show options that have permission (isEnabled or disabled due to mutual exclusivity)
  // Hide completely if no permission
  const filteredOptions = useMemo(() => {
    return optionsWithState
      .filter((opt) => {
        // Show if has permission (even if disabled due to mutual exclusivity)
        // Hide if no permission at all
        const originalOption = options.find((o) => o.value === opt.value)
        if (!originalOption) return false

        // Hide options where user has no permission
        if (!opt.hasPermission) {
          return false // Don't render - no permission
        }
        return true // Render (may be disabled due to mutual exclusivity)
      })
      .map((opt) => ({
        ...opt,
        // Mark as disabled only if mutually exclusive (has permission but disabled)
        disabled: opt.isDisabled && opt.hasPermission,
        tooltipText: opt.disabledReason
      }))
  }, [optionsWithState, options])

  // Don't render the control if no options are available
  if (filteredOptions.length === 0) {
    return null
  }

  return <OutreachFilterControl options={filteredOptions} categoryTitle={categoryTitle} {...props} />
}

OutreachFilterControlWrapper.propTypes = {
  filterKey: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  categoryTitle: PropTypes.string
}

/**
 * FilterControl - Generic filter control component
 *
 * Renders the appropriate filter control based on the filter type.
 * Acts as a factory/wrapper component for different filter types.
 * Each filter type includes FilterHeader + specific control logic.
 *
 * @param {Object} props
 * @param {Object} props.filter - Filter configuration object
 * @param {string} props.filter.type - Type of filter ('range', 'checkbox', 'toggle', 'radio')
 * @param {string} props.filter.key - Unique identifier for the filter
 * @param {string} props.filter.name - Display label for the filter
 * @param {*} props.filter.value - Current value of the filter
 * @param {Object} props.filter.options - Type-specific configuration options
 * @param {Function} props.onChange - Callback when filter value changes (newValue)
 * @param {Function} props.onRemove - Callback when filter is removed
 * @param {Object} props.rest - Additional props passed to specific filter components
 */
const FilterControl = ({filter, onChange, onRemove, ...rest}) => {
  const theme = useTheme()
  // Get toggle options config from Redux store
  const filterConfig = useSelector(selectFilterConfig)
  const toggleOptionsValue = filterConfig?.toggleOptionsValue || {all: 2, yes: 1, no: 0}

  // Get category title for analytics (called before early return to follow Rules of Hooks)
  const categoryTitle = useMemo(() => {
    if (!filterConfig?.data || !filter?.categoryId) return 'Unknown Category'
    const category = filterConfig.data.find((cat) => cat.id === filter.categoryId)
    return category?.title || 'Unknown Category'
  }, [filterConfig, filter?.categoryId])

  if (!filter) {
    console.warn('FilterControl: No filter provided')
    return null
  }
  const {type, key, name, customName, value, options, desc, identifier, categoryId, disabled, disabledReason} = filter

  let filterLabel = customName || name

  // (Percentile) suffix for category 2 filters
  if (categoryId === '2') {
    filterLabel = `${filterLabel} (Percentile)`
  }
  // Only show description tooltip if name is NOT customized
  const filterDescription = customName ? undefined : desc

  const handleChange = (event, newValue) => {
    // For checkbox filters, newValue is already the array of selected values
    // For range sliders, newValue is [min, max]
    // For toggles, newValue is the selected radio value (all/yes/no)
    // For radio, newValue is the selected string/number value
    if (onChange) {
      onChange(newValue === undefined ? event : newValue)
    }
  }

  switch (type) {
    case 'range': {
      // Prepare range filter props
      const rangeProps = {
        label: filterLabel,
        description: filterDescription,
        filterKey: key, // Used for state management
        identifier, // Used for API calls (e.g., 'hospital', 'childcare')
        categoryId, // Used to detect filter type (geographic, climate, etc.)
        useGradient: filter.isGradient || false,
        step: options?.step || 1,
        showCustomLabels: filter?.isGradient,
        onChangeCommitted: handleChange,
        onRemove
      }

      // Only pass min/max/value if explicitly defined (enables auto-detection)
      // If options.min and options.max are defined → Simple mode
      // If not defined → Auto-fetch mode (for geographic/climate filters)
      if (options?.min !== undefined && options?.max !== undefined) {
        rangeProps.min = options.min
        rangeProps.max = options.max
        rangeProps.value = value || [options.min, options.max]
        rangeProps.minLabel = options.minLabel || ''
        rangeProps.maxLabel = options.maxLabel || ''
      }
      // If value is provided without min/max, use it
      // But only if the value doesn't contain null (for dynamic filters with pending values)
      else if (value && !value.includes(null)) {
        rangeProps.value = value
      }

      return <RangeFilterControl {...rangeProps} {...rest} />
    }

    case 'checkbox':
      return (
        <CheckboxFilterControl
          label={filterLabel}
          description={filterDescription}
          options={options || []}
          selectedValues={value || []}
          onChange={handleChange}
          onRemove={onRemove}
          filterKey={key}
          categoryId={categoryId} // Category ID for determining filter type
          {...rest}
        />
      )

    case 'toggle': {
      // Use radio buttons with All/Yes/No options for toggle filters
      // Use numeric values from toggleOptionsValue config: all=2, yes=1, no=0
      // This format is required for API compatibility
      const toggleOptions = options?.options || [
        {label: 'All', value: toggleOptionsValue.all},
        {label: 'Yes', value: toggleOptionsValue.yes},
        {label: 'No', value: toggleOptionsValue.no}
      ]

      return (
        <RadioFilterControl
          label={filterLabel}
          description={disabled && disabledReason ? disabledReason : filterDescription}
          options={toggleOptions}
          value={value ?? options?.defaultValue ?? toggleOptionsValue.all}
          onChange={handleChange}
          onRemove={onRemove}
          orientation={options?.orientation || 'horizontal'}
          disabled={disabled}
          {...rest}
        />
      )
    }

    case 'radio':
      return (
        <RadioFilterControl
          label={filterLabel}
          description={filterDescription}
          options={options?.options || []}
          value={value || options?.defaultValue || null}
          onChange={handleChange}
          onRemove={onRemove}
          orientation={options?.orientation || 'vertical'}
          {...rest}
        />
      )

    case 'outreach':
      // Outreach filter with checkboxes + enrolled status (All/Yes/No)
      // Value structure: { selected: ['SNAP', 'Medicare'], enrolledValue: 2 }
      // Options are processed in OutreachFilterControlWrapper to handle permissions
      return (
        <OutreachFilterControlWrapper
          filterKey={key}
          label={filterLabel}
          description={filterDescription}
          options={options || []}
          enrolledLabel={filter.enrolledLabel || 'Enrolled'}
          value={value || {selected: [], enrolledValue: 2}}
          onChange={handleChange}
          onRemove={onRemove}
          categoryTitle={categoryTitle}
          {...rest}
        />
      )

    default:
      console.warn(`FilterControl: Unknown filter type "${type}" for filter "${key}"`)
      return <div style={{padding: '8px', color: theme.palette.hpBase}}>Unknown filter type: {type}</div>
  }
}

FilterControl.propTypes = {
  filter: PropTypes.shape({
    type: PropTypes.oneOf(['range', 'checkbox', 'toggle', 'radio', 'outreach']).isRequired,
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    customName: PropTypes.string, // Custom name from backend
    desc: PropTypes.string,
    value: PropTypes.any,
    options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // Object for most filters, array for outreach
    isGradient: PropTypes.bool,
    identifier: PropTypes.string, // API identifier for data-fetching filters
    categoryId: PropTypes.string, // Category ID from config (e.g., "communitySupportAndLocation")
    enrolledLabel: PropTypes.string, // Label for enrolled status (outreach filters),
    disabled: PropTypes.bool,
    disabledReason: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func,
  onRemove: PropTypes.func
}

export default FilterControl
